import React from 'react';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';

interface GlobalDeliveryProps {
  setCurrentPage: (page: string) => void;
}

const GlobalDelivery: React.FC<GlobalDeliveryProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: LucideIcons.Globe,
      title: "Global Reach",
      description: "24/7 security operations and support across multiple time zones and regions.",
      items: [
        "Worldwide coverage",
        "Local expertise",
        "Regional compliance",
        "Time zone optimization"
      ]
    },
    {
      icon: LucideIcons.Users,
      title: "Expert Teams",
      description: "Dedicated security professionals with diverse expertise and certifications.",
      items: [
        "Certified professionals",
        "Industry specialists",
        "Technical experts",
        "Security architects"
      ]
    },
    {
      icon: LucideIcons.Clock,
      title: "Round-the-Clock Support",
      description: "Continuous monitoring and support through our global delivery centers.",
      items: [
        "24/7 monitoring",
        "Rapid response",
        "Continuous support",
        "Proactive maintenance"
      ]
    },
    {
      icon: LucideIcons.Zap,
      title: "Rapid Response",
      description: "Swift incident response and resolution through our global network.",
      items: [
        "Quick resolution",
        "Emergency support",
        "Incident handling",
        "Problem management"
      ]
    }
  ];

  const capabilities = [
    {
      icon: LucideIcons.Shield,
      title: "Security Operations",
      description: "Comprehensive security monitoring and management.",
      items: [
        "24/7 security monitoring",
        "Incident management",
        "Threat hunting",
        "Security analytics"
      ]
    },
    {
      icon: LucideIcons.AlertTriangle,
      title: "Incident Response",
      description: "Swift and effective response to security incidents.",
      items: [
        "Rapid detection",
        "Incident containment",
        "Threat elimination",
        "Recovery support"
      ]
    },
    {
      icon: LucideIcons.Database,
      title: "Threat Intelligence",
      description: "Advanced threat detection and analysis capabilities.",
      items: [
        "Threat feeds integration",
        "Intelligence analysis",
        "Threat correlation",
        "Risk assessment"
      ]
    }
  ];

  const benefits = [
    {
      icon: LucideIcons.Globe,
      title: "Global Reach",
      description: "Strategic locations worldwide to serve clients across all time zones"
    },
    {
      icon: LucideIcons.Clock,
      title: "24/7 Operations",
      description: "Round-the-clock security monitoring and support services"
    },
    {
      icon: LucideIcons.Users,
      title: "Local Expertise",
      description: "Teams with deep understanding of regional security landscapes"
    },
    {
      icon: LucideIcons.Shield,
      title: "Unified Security",
      description: "Consistent security standards across all global locations"
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <LucideIcons.Globe className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Global Delivery Model</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Delivering world-class security services through our global network of security operations centers
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Global Delivery Features</span>
            </h2>
            <p className="text-lg text-muted">
              Key features of our global delivery model
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="glass-panel rounded-xl p-6 hover-card"
                >
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                    <IconComponent className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-muted mb-6">
                    {feature.description}
                  </p>
                  <ul className="space-y-3">
                    {feature.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center">
                        <LucideIcons.CheckCircle className="h-5 w-5 text-primary mr-3" />
                        <span className="text-muted">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Capabilities Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Core Capabilities</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive security operations capabilities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => {
              const IconComponent = capability.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="glass-panel rounded-xl p-8 hover-card"
                >
                  <div className="flex items-center mb-6">
                    <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold ml-4">{capability.title}</h3>
                  </div>
                  
                  <p className="text-muted mb-6">{capability.description}</p>

                  <ul className="space-y-3">
                    {capability.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center">
                        <LucideIcons.CheckCircle className="h-5 w-5 text-primary mr-3" />
                        <span className="text-muted">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Global Presence Benefits</span>
            </h2>
            <p className="text-lg text-muted">
              Advantages of our worldwide network of security operations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="glass-panel rounded-xl p-6 hover-card"
                >
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                    <IconComponent className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-muted">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">Ready to Go Global?</h2>
              <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
                Experience the benefits of our global delivery model
              </p>
              <button className="btn-primary">
                <span className="flex items-center justify-center">
                  <span onClick={() => setCurrentPage('get-started')}>Request Consultation</span>
                  <LucideIcons.ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GlobalDelivery;